import type { ReviewClosingBreakdownLine, ReviewClosingRow } from '../../../OrderForm/types';
import { formatCurrency } from '../../../OrderForm/utils/reviewTotals';

const TYPE_LABELS: Record<string, string> = {
  MATERIAL: 'Matéria Prima',
  PRODUCT: 'Produto Final',
  RESOURCE: 'Insumos',
  SERVICE: 'Serviço',
};

const TYPE_CHIP: Record<string, string> = {
  MATERIAL: 'MP',
  PRODUCT: 'PE',
  RESOURCE: 'Insumo',
  SERVICE: 'Serviço',
};

/** Só o item salvo entra no fechamento, mas o índice é o do array inteiro: é ele que o rateio e a
 *  navegação para o card usam. */
export const buildSteelClosingRows = (items: any[]): ReviewClosingRow[] => (items || [])
  .map((it: any, index: number) => ({ it, index }))
  .filter(({ it }) => it?.isSaved)
  .map(({ it, index }) => ({
    index,
    name: `${index + 1} · ${TYPE_LABELS[String(it?.itemType)] || String(it?.itemType)}`,
    detail: it?.nomeCustomizado || undefined,
    pieces: Number(it?.quantityPieces) > 0 ? Number(it.quantityPieces) : null,
    registeredPrice: it?.itemPrice != null ? Number(it.itemPrice) : 0,
    sellingPrice: it?.priceDetails?.sellingPrice != null ? Number(it.priceDetails.sellingPrice) : null,
  }));

const materialLine = (source: any, qty: number | null, unitPrice: number | null, unitName: string): ReviewClosingBreakdownLine => {
  const desc = [source?.materiaPrima, source?.stockDescription].filter(Boolean).join(' — ');
  const value = qty != null && unitPrice != null && qty > 0 && unitPrice > 0 ? qty * unitPrice : null;
  return {
    chip: TYPE_CHIP.MATERIAL,
    desc: desc || 'Matéria-prima',
    formula: qty != null && unitPrice != null && qty > 0 && unitPrice > 0
      ? `${qty.toLocaleString('pt-BR')}${unitName ? ` ${unitName}` : ''} × ${formatCurrency(unitPrice)}`
      : undefined,
    value,
  };
};

const serviceLine = (sub: any): ReviewClosingBreakdownLine => {
  const desc = sub?.stage?.servico || sub?.nomeCustomizado || 'Serviço';
  const cost = sub?.cost != null ? Number(sub.cost) : null;
  const markup = sub?.markup != null ? Number(sub.markup) : null;
  const qty = sub?.stage?.quantidade != null ? Number(sub.stage.quantidade) : (sub?.quantity != null ? Number(sub.quantity) : null);
  const value = sub?.itemPrice != null ? Number(sub.itemPrice)
    : (sub?.priceDetails?.itemSubtotal != null ? Number(sub.priceDetails.itemSubtotal) : null);
  const formula = cost != null && cost > 0
    ? `custo ${formatCurrency(cost)}${qty != null && qty > 0 ? ` × ${qty.toLocaleString('pt-BR')}` : ''}${markup != null && markup > 0 ? ` × ${markup.toLocaleString('pt-BR')}` : ''}`
    : undefined;
  return { chip: TYPE_CHIP.SERVICE, desc, formula, value };
};

/** Monta as linhas de composição de um item a partir do form state (sem lógica de escrita). */
export const buildSteelBreakdown = (it: any): ReviewClosingBreakdownLine[] => {
  const lines: ReviewClosingBreakdownLine[] = [];
  const unitName = it?.unitOfMeasurementName || '';

  const itemType = String(it?.itemType || '');
  const qty = it?.quantity != null ? Number(it.quantity) : null;
  const unitPrice = it?.priceDetails?.unitPrice != null ? Number(it.priceDetails.unitPrice) : null;

  if (itemType === 'MATERIAL' && it?.feedstock) {
    lines.push(materialLine(it.feedstock, qty, unitPrice, unitName));
  } else if (itemType === 'PRODUCT' && it?.finalProduct?.produto) {
    lines.push({ chip: TYPE_CHIP.PRODUCT, desc: it.finalProduct.produto, value: null });
  } else if (itemType === 'RESOURCE' && it?.resource?.insumo) {
    const value = qty != null && unitPrice != null && qty > 0 && unitPrice > 0 ? qty * unitPrice : null;
    lines.push({ chip: TYPE_CHIP.RESOURCE, desc: it.resource.insumo, value });
  } else if (itemType === 'SERVICE') {
    lines.push(serviceLine(it));
  }

  (Array.isArray(it?.subItems) ? it.subItems : []).forEach((sub: any) => {
    const subType = String(sub?.itemType || '');
    if (subType === 'SERVICE') {
      lines.push(serviceLine(sub));
    } else if (subType === 'MATERIAL') {
      const subQty = sub?.feedstock?.quantidade != null ? Number(sub.feedstock.quantidade) : null;
      const subUnitPrice = sub?.priceDetails?.unitPrice != null ? Number(sub.priceDetails.unitPrice) : null;
      lines.push(materialLine(sub?.feedstock, subQty, subUnitPrice, unitName));
    } else if (sub?.nomeCustomizado || sub?.resource?.insumo) {
      lines.push({
        chip: TYPE_CHIP[subType] || subType,
        desc: sub?.resource?.insumo || sub?.nomeCustomizado,
        value: sub?.itemPrice != null ? Number(sub.itemPrice) : null,
      });
    }
  });

  return lines;
};
